import type { RoleSource } from '../../types/framework'

export const coo: RoleSource = {
  id: 'coo',
  name: 'Chief Operating Officer',
  group: 'Executive',
  domains: [
    {
      name: 'Capacity, Productivity & Resource Planning',
      characteristic: [
        [601, "Resource Stewardship", "Demonstrates resource stewardship."],
        [602, "Capacity Planning Discipline", "Demonstrates capacity-planning discipline."],
        [603, "Productivity Acumen", "Demonstrates understanding of productivity drivers."],
        [604, "Demand Flexibility", "Demonstrates flexibility under changing demand."],
        [605, "Realistic Planning Judgement", "Demonstrates realistic planning judgment."],
        [606, "Utilisation and Outcome Focus", "Demonstrates focus on utilization and outcomes."],
      ],
      behaviour: [
        [607, "Financial Planning and Forecasting", "Matches workforce, equipment and capacity to demand forecasts."],
        [608, "Capacity and Resource Optimisation", "Identifies productivity constraints and underutilized resources."],
        [609, "Crisis Leadership and Resilience", "Balances utilization targets with resilience and service requirements."],
        [610, "Capacity and Resource Optimisation", "Updates capacity plans when demand, staffing or supply changes."],
        [611, "Capacity and Resource Optimisation", "Uses productivity measures to guide operational interventions."],
        [612, "Capacity and Resource Optimisation", "Coordinates resource allocation across competing business priorities."],
      ],
    },
    {
      name: 'Cross-Functional Coordination & Integration',
      characteristic: [
        [613, "Collaboration and Relationship Building", "Demonstrates collaborative orientation."],
        [614, "Enterprise-wide perspective", "Demonstrates enterprise-wide perspective."],
        [615, "Conflict-management ability", "Demonstrates conflict-management ability."],
        [616, "Respect for functional expertise", "Demonstrates respect for functional expertise."],
        [617, "Systems thinking", "Demonstrates systems thinking."],
        [618, "Bias toward shared outcomes", "Demonstrates bias toward shared outcomes."],
      ],
      behaviour: [
        [619, "Cross-Functional Coordination", "Establishes clear cross-functional ownership for shared processes."],
        [620, "Execution and Delivery", "Resolves dependencies and conflicts that impede delivery."],
        [621, "Aligns functions around common targets and operating rhythms", "Aligns functions around common targets and operating rhythms."],
        [622, "Ensuring hand-offs and decision rights are understood", "Ensures hand-offs and decision rights are understood."],
        [623, "Shares operational information with relevant teams in time", "Shares operational information with relevant teams in time to act."],
        [624, "Addresses silo behaviour that harms enterprise performance", "Addresses silo behaviour that harms enterprise performance."],
      ],
    },
    {
      name: 'Operating Model & Organizational Effectiveness',
      characteristic: [
        [625, "Organizational-design awareness", "Demonstrates organizational-design awareness."],
        [626, "Clarity about accountability", "Demonstrates clarity about accountability."],
        [627, "Adaptability", "Demonstrates adaptability."],
        [628, "Decision-Making and Judgement", "Demonstrates practical judgment about centralization."],
        [629, "Focus on decision speed", "Demonstrates focus on decision speed."],
        [630, "Understanding of capability requirements", "Demonstrates understanding of capability requirements."],
      ],
      behaviour: [
        [631, "Review of structures and accountabilities against operating needs", "Reviews structures and accountabilities against operating needs."],
        [632, "Clarifies roles, decision rights and escalation paths", "Clarifies roles, decision rights and escalation paths."],
        [633, "Removes unnecessary layers or duplicated responsibilities", "Removes unnecessary layers or duplicated responsibilities."],
        [634, "Aligns governance routines with the pace and complexity", "Aligns governance routines with the pace and complexity of operations."],
        [635, "Operating Model and Process Effectiveness", "Assesses whether the operating model supports customer and business outcomes."],
        [636, "Change Management", "Implements structural changes with clear ownership and transition plans."],
      ],
    },
    {
      name: 'Operational Data, Metrics & Performance Reviews',
      characteristic: [
        [637, "Data-informed mindset", "Demonstrates data-informed mindset."],
        [638, "Analytical Thinking and Evidence Use", "Demonstrates analytical discipline."],
        [639, "Focus on leading indicators", "Demonstrates focus on leading indicators."],
        [640, "Healthy skepticism about metrics", "Demonstrates healthy skepticism about metrics."],
        [641, "Results orientation", "Demonstrates results orientation."],
        [642, "Commitment to transparent reporting", "Demonstrates commitment to transparent reporting."],
      ],
      behaviour: [
        [643, "Establishment of balanced operational scorecard with definitions", "Establishes a balanced operational scorecard with clear definitions."],
        [644, "Review of trends and exceptions at an cadence", "Reviews trends and exceptions at an appropriate cadence."],
        [645, "Distinguishes symptoms from underlying performance drivers", "Distinguishes symptoms from underlying performance drivers."],
        [646, "Execution and Delivery", "Uses leading indicators to anticipate delivery or capacity problems."],
        [647, "Performance Management and Monitoring", "Assigns actions and deadlines following performance reviews."],
        [648, "Data Governance and Analytics", "Validates data quality and avoids incentives that encourage metric manipulation."],
      ],
    },
    {
      name: 'Operational Excellence & Process Improvement',
      characteristic: [
        [649, "Continuous-improvement mindset", "Demonstrates continuous-improvement mindset."],
        [650, "Attention to process discipline", "Demonstrates attention to process discipline."],
        [651, "Root-cause orientation", "Demonstrates root-cause orientation."],
        [652, "Practical problem-solving", "Demonstrates practical problem-solving."],
        [653, "Quality consciousness", "Demonstrates quality consciousness."],
        [654, "Willingness to challenge waste", "Demonstrates willingness to challenge waste."],
      ],
      behaviour: [
        [655, "Analytical Thinking and Evidence Use", "Maps and improves end-to-end processes using evidence."],
        [656, "Removes bottlenecks, duplication and non-value-adding work", "Removes bottlenecks, duplication and non-value-adding work."],
        [657, "Analytical Thinking and Evidence Use", "Uses root-cause analysis rather than treating symptoms."],
        [658, "Setting of measurable improvement targets for cost, speed", "Sets measurable improvement targets for cost, speed and quality."],
        [659, "Standardizes effective practices across relevant operations", "Standardizes effective practices across relevant operations."],
        [660, "Operating Model and Process Effectiveness", "Verifies that process improvements are sustained over time."],
      ],
    },
    {
      name: 'Operational Risk, Safety & Business Continuity',
      characteristic: [
        [661, "Safety consciousness", "Demonstrates safety consciousness."],
        [662, "Risk sensitivity", "Demonstrates risk sensitivity."],
        [663, "Crisis Leadership and Resilience", "Demonstrates resilience under disruption."],
        [664, "Accountability for operational controls", "Demonstrates accountability for operational controls."],
        [665, "Preparedness mindset", "Demonstrates preparedness mindset."],
        [666, "Willingness to escalate hazards", "Demonstrates willingness to escalate hazards."],
      ],
      behaviour: [
        [667, "Identification of and manages critical operational and safety", "Identifies and manages critical operational and safety risks."],
        [668, "Planning and Prioritisation", "Maintains practical continuity and recovery plans."],
        [669, "Ensuring incidents and near misses are reported and", "Ensures incidents and near misses are reported and investigated."],
        [670, "Tests readiness for disruption affecting people, facilities or", "Tests readiness for disruption affecting people, facilities or suppliers."],
        [671, "Escalates material operational exposures and assigns remediation owners", "Escalates material operational exposures and assigns remediation owners."],
        [672, "Monitoring of whether risk controls work in daily", "Monitors whether risk controls work in daily operations."],
      ],
    },
    {
      name: 'Operational Strategy & Business Alignment',
      characteristic: [
        [673, "Operational systems thinking", "Demonstrates operational systems thinking."],
        [674, "Understanding of value streams", "Demonstrates understanding of value streams."],
        [675, "Customer and User Focus", "Demonstrates customer and business orientation."],
        [676, "Pragmatism in setting priorities", "Demonstrates pragmatism in setting priorities."],
        [677, "Long-term operating perspective", "Demonstrates long-term operating perspective."],
        [678, "Ownership of enterprise outcomes", "Demonstrates ownership of enterprise outcomes."],
      ],
      behaviour: [
        [679, "Strategic Thinking and Alignment", "Translates strategy into an integrated operating agenda."],
        [680, "Aligns operating priorities across functions and business units", "Aligns operating priorities across functions and business units."],
        [681, "Identification of operational capabilities required to support strategic", "Identifies operational capabilities required to support strategic goals."],
        [682, "Balancing of cost, service, quality and growth trade-offs", "Balances cost, service, quality and growth trade-offs."],
        [683, "Strategic Thinking and Alignment", "Reviews operating strategy as demand or business conditions change."],
        [684, "Tracking of whether operational initiatives deliver strategic benefits", "Tracks whether operational initiatives deliver strategic benefits."],
      ],
    },
    {
      name: 'People Leadership & Frontline Engagement',
      characteristic: [
        [685, "Respect for frontline expertise", "Demonstrates respect for frontline expertise."],
        [686, "Commitment to employee development", "Demonstrates commitment to employee development."],
        [687, "Diversity, Equity and Inclusion", "Demonstrates fairness."],
        [688, "Visibility and accessibility", "Demonstrates visibility and accessibility."],
        [689, "Capacity and Resource Optimisation", "Demonstrates awareness of workforce capacity."],
        [690, "Belief in accountability with support", "Demonstrates belief in accountability with support."],
      ],
      behaviour: [
        [691, "Engages frontline teams to understand operational barriers", "Engages frontline teams to understand operational barriers."],
        [692, "Setting of performance expectations for operational leaders", "Sets clear performance expectations for operational leaders."],
        [693, "Leadership Effectiveness", "Develops supervisors and managers in people and performance leadership."],
        [694, "Addresses unsafe conduct and persistent underperformance fairly", "Addresses unsafe conduct and persistent underperformance fairly."],
        [695, "Execution and Delivery", "Uses workforce feedback to improve working conditions and execution."],
        [696, "Capacity and Resource Optimisation", "Recognizes teams for measurable service, quality and productivity improvements."],
      ],
    },
    {
      name: 'Quality, Standards & Continuous Improvement',
      characteristic: [
        [697, "Commitment to quality", "Demonstrates commitment to quality."],
        [698, "Attention to detail", "Demonstrates attention to detail."],
        [699, "Prevention-oriented mindset", "Demonstrates prevention-oriented mindset."],
        [700, "Customer and User Focus", "Demonstrates customer impact awareness."],
        [701, "Discipline in standards", "Demonstrates discipline in standards."],
        [702, "Continuous Learning and Improvement", "Demonstrates openness to learning from defects."],
      ],
      behaviour: [
        [703, "Establishment of measurable quality standards and ownership", "Establishes measurable quality standards and ownership."],
        [704, "Monitoring of defects, rework, complaints and process variation", "Monitors defects, rework, complaints and process variation."],
        [705, "Investigates significant quality issues and implements corrective action", "Investigates significant quality issues and implements corrective action."],
        [706, "Execution and Delivery", "Ensures procedures and training support consistent execution."],
        [707, "Application of audits and operational data to identify", "Uses audits and operational data to identify systemic weaknesses."],
        [708, "Checks that corrective actions prevent recurrence", "Checks that corrective actions prevent recurrence."],
      ],
    },
    {
      name: 'Service Delivery & Customer Experience',
      characteristic: [
        [709, "Customer and User Focus", "Demonstrates customer-centricity."],
        [710, "Reliability orientation", "Demonstrates reliability orientation."],
        [711, "Awareness of service expectations", "Demonstrates awareness of service expectations."],
        [712, "Decision-Making and Judgement", "Demonstrates balanced service-cost judgment."],
        [713, "Responsiveness to feedback", "Demonstrates responsiveness to feedback."],
        [714, "Commitment to service standards", "Demonstrates commitment to service standards."],
      ],
      behaviour: [
        [715, "Commercial and Business Acumen", "Defines service-level measures aligned with customer needs."],
        [716, "Monitoring of service quality, timeliness and reliability", "Monitors service quality, timeliness and reliability."],
        [717, "Resolves recurring service failures through systemic action", "Resolves recurring service failures through systemic action."],
        [718, "Customer and User Focus", "Coordinates hand-offs across teams to reduce customer friction."],
        [719, "Application of complaints and feedback to prioritize service", "Uses complaints and feedback to prioritize service improvements."],
        [720, "Stakeholder Engagement and Accountability", "Communicates service risks and recovery actions to stakeholders."],
      ],
    },
    {
      name: 'Supply Chain, Procurement & Logistics',
      characteristic: [
        [721, "Supply-chain awareness", "Demonstrates supply-chain awareness."],
        [722, "Commercial discipline", "Demonstrates commercial discipline."],
        [723, "Decision-Making and Judgement", "Demonstrates supplier relationship judgment."],
        [724, "Crisis Leadership and Resilience", "Demonstrates resilience mindset."],
        [725, "Cost and service trade-off awareness", "Demonstrates cost and service trade-off awareness."],
        [726, "Integrity and Ethical Leadership", "Demonstrates ethical procurement orientation."],
      ],
      behaviour: [
        [727, "Planning and Prioritisation", "Aligns sourcing and logistics plans with demand and service requirements."],
        [728, "Monitoring of supplier performance, concentration and continuity risks", "Monitors supplier performance, concentration and continuity risks."],
        [729, "Negotiates procurement outcomes against total cost and value", "Negotiates procurement outcomes against total cost and value criteria."],
        [730, "Capacity and Resource Optimisation", "Maintains appropriate inventory and capacity policies."],
        [731, "Ensuring procurement decisions follow transparent controls and delegated", "Ensures procurement decisions follow transparent controls and delegated authority."],
        [732, "Development of alternatives for critical suppliers and vulnerable", "Develops alternatives for critical suppliers and vulnerable supply routes."],
      ],
    },
    {
      name: 'Transformation & Change Implementation',
      characteristic: [
        [733, "Adaptability", "Demonstrates adaptability."],
        [734, "Persistence", "Demonstrates persistence."],
        [735, "Capacity and Resource Optimisation", "Demonstrates realism about change capacity."],
        [736, "Continuous Learning and Improvement", "Demonstrates learning orientation."],
        [737, "Comfort with ambiguity", "Demonstrates comfort with ambiguity."],
        [738, "Focus on benefits realization", "Demonstrates focus on benefits realization."],
      ],
      behaviour: [
        [739, "Crisis Leadership and Resilience", "Sequences operational change to protect business continuity."],
        [740, "Execution and Delivery", "Assigns accountable owners, milestones and measurable benefits."],
        [741, "Execution and Delivery", "Engages affected teams early and addresses implementation concerns."],
        [742, "Change Management", "Coordinates technology, process and people changes as one program."],
        [743, "Change Management", "Tracks adoption and operational benefits after launch."],
        [744, "Analytical Thinking and Evidence Use", "Adjusts or stops initiatives when evidence shows they are not delivering."],
      ],
    },
  ],
}
